import { computed, shallowRef, type ComputedRef } from 'vue';
import { THEME_ACTIVE_ATTR } from '../core/tokens';
import { createThemeCssVars } from '../core/cssVars';
import { resolveTheme } from '../core/resolve';
import type { IResolvedTheme, ThemeVantPresetType } from '../types';

/** 由 themeVitePlugin 在构建期注入：app uno.config 中主题的运行时快照 */
declare const __H5_THEME_DEFAULTS__: IResolvedTheme | undefined;

export interface IThemeRuntime {
  /** app 默认主题（与 Uno 同源） */
  readonly defaults: IResolvedTheme;
  /** 当前生效主题：未激活时为 defaults */
  readonly theme: ComputedRef<IResolvedTheme>;
  /** 是否已有运行时主题写入文档 */
  readonly isActive: ComputedRef<boolean>;
  /** 归一化（缺失/非法回退 defaults）后写入根节点，返回生效快照 */
  apply(input: unknown): IResolvedTheme;
  /** 精确恢复写入前的 inline 值，回到静态默认主题 */
  reset(): void;
}

function readDefaults(): IResolvedTheme {
  if (typeof __H5_THEME_DEFAULTS__ === 'undefined') {
    throw new Error('[theme] 缺少运行时默认主题：请在该 app 的 vite.config 中注册 themeVitePlugin(theme)');
  }
  return __H5_THEME_DEFAULTS__;
}

/** 只恢复本控制器写过的属性，不干扰其他 root inline 样式 */
function createStyleController(root: HTMLElement, vant: ThemeVantPresetType) {
  const originals = new Map<string, { value: string; priority: string }>();
  const originalAttribute = root.getAttribute(THEME_ACTIVE_ATTR);
  return {
    apply(theme: IResolvedTheme) {
      for (const [key, value] of Object.entries(createThemeCssVars(theme, vant))) {
        if (!originals.has(key)) {
          originals.set(key, { value: root.style.getPropertyValue(key), priority: root.style.getPropertyPriority(key) });
        }
        root.style.setProperty(key, value);
      }
      root.setAttribute(THEME_ACTIVE_ATTR, 'active');
    },
    reset() {
      for (const [key, original] of originals) {
        if (original.value) root.style.setProperty(key, original.value, original.priority);
        else root.style.removeProperty(key);
      }
      originals.clear();
      if (originalAttribute === null) root.removeAttribute(THEME_ACTIVE_ATTR);
      else root.setAttribute(THEME_ACTIVE_ATTR, originalAttribute);
    }
  };
}

function createThemeRuntime(vant: ThemeVantPresetType): IThemeRuntime {
  const defaults = readDefaults();
  const current = shallowRef<IResolvedTheme | null>(null);
  let controller: ReturnType<typeof createStyleController> | undefined;
  return {
    defaults,
    theme: computed(() => current.value ?? defaults),
    isActive: computed(() => current.value !== null),
    apply(input) {
      const snapshot = resolveTheme(input, defaults);
      controller ??= createStyleController(document.documentElement, vant);
      controller.apply(snapshot);
      current.value = snapshot;
      return snapshot;
    },
    reset() {
      controller?.reset();
      current.value = null;
    }
  };
}

let instance: { runtime: IThemeRuntime; vant: ThemeVantPresetType } | undefined;

/**
 * 当前文档唯一的主题运行时（host / embed / useTheme 共用）。
 * vant 以首次创建为准：应在入口先调用 createHostTheme / installEmbedTheme
 */
export function getThemeRuntime(options: { vant?: ThemeVantPresetType } = {}): IThemeRuntime {
  if (!instance) {
    const vant = options.vant ?? 'basic';
    instance = { runtime: createThemeRuntime(vant), vant };
  } else if (import.meta.env.DEV && options.vant && options.vant !== instance.vant) {
    console.warn(`[theme] runtime 已按 vant='${instance.vant}' 创建，忽略 '${options.vant}'`);
  }
  return instance.runtime;
}
